import { useDebounceFn } from '@vueuse/core'
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { DEFAULT_DEBOUNCE_DELAY } from '@/utils/debounce'
import { storeToRefs } from 'pinia'

import { useCalculationForm } from '@/components/calculation-form/composable/useCalculationForm'
import type { CalculationFormMixinProps } from '@/components/calculation-form/type/type'
import { useBuildingBlockStore, type BuildingBlockDto } from '@/stores/buildingBlock'
import { useQuantityCodeStore } from '@/stores/quantityCode'
import { useTransportConfigStore } from '@/stores/transportConfig'
import { useCalculationStore } from '@/stores/calculation'
import { useConfirmDeleteStore } from '@/stores/confirmDelete'
import type { TransportConfig } from '@/stores/type/transportConfig.type'
import {
  ALL_TRANSPORT_CONFIG_VALUE,
  buildTransportMissingMessages,
  createEmptyTransportConfigModel,
  createSelectOptions,
  DEFAULT_TRANSPORT_DISTANCE,
  isTransportConfigComplete,
  NEW_TRANSPORT_CONFIG_ID,
  resolveTransportCategory,
  resolveTransportNoticeTitle,
  type NewTransportConfigModel
} from '@/components/calculation-form/transport-configuration/utils/transportConfigurationUtils'

export function useTransportConfiguration(props: CalculationFormMixinProps) {
  const { isValid, isReadOnly } = useCalculationForm(props)

  const route = useRoute()
  const calculationId = computed(() => Number(route.params.calculationId))

  const buildingBlockStore = useBuildingBlockStore()
  const quantityCodeStore = useQuantityCodeStore()
  const transportConfigStore = useTransportConfigStore()
  const calculationStore = useCalculationStore()
  const confirmDeleteStore = useConfirmDeleteStore()

  const { transportConfigSwitchNotice } = storeToRefs(calculationStore)

  const newConfig = ref<NewTransportConfigModel>(createEmptyTransportConfigModel())

  const handleLoading = () => {
    return (
      buildingBlockStore.fetchLoading ||
      quantityCodeStore.fetchLoading ||
      transportConfigStore.fetchLoading
    )
  }

  const existingConfigs = computed(() => {
    return transportConfigStore.items.filter(
      (config: TransportConfig) => config.calculation === calculationId.value
    )
  })

  const availableBuildingBlocks = computed(() => {
    const allowedIds = new Set<number>()

    for (const block of buildingBlockStore.items) {
      if (block.calculation === calculationId.value && block.selected) {
        allowedIds.add(block.id)
      }
    }

    for (const config of existingConfigs.value) {
      if (typeof config.building_block === 'number') {
        allowedIds.add(config.building_block)
      }
    }

    const availableBuildingBlockItems = buildingBlockStore.items.filter((block: BuildingBlockDto) =>
      allowedIds.has(block.id)
    )

    return createSelectOptions(availableBuildingBlockItems, calculationId.value)
  })

  const availableDikeSections = computed(() => {
    return createSelectOptions(quantityCodeStore.items, calculationId.value)
  })

  const availableBuildingBlockValueSet = computed(() => {
    const values = new Set<string>()

    for (const item of availableBuildingBlocks.value) {
      values.add(String(item.value))
    }

    return values
  })

  const transportNoticeTitle = computed(() => {
    return resolveTransportNoticeTitle(transportConfigSwitchNotice.value)
  })

  const transportMissingMessages = computed(() => {
    return buildTransportMissingMessages(transportConfigSwitchNotice.value)
  })

  watch(
    existingConfigs,
    (configs) => {
      isValid.value = configs.length === 0 || configs.every(isTransportConfigComplete)
    },
    { immediate: true }
  )

  onMounted(async () => {
    const query = { calculation: calculationId.value }
    await Promise.all([
      buildingBlockStore.fetchItems?.(query),
      quantityCodeStore.fetchItems?.(query),
      transportConfigStore.fetchItems?.(query)
    ])
  })

  const addTransportConfig = async () => {
    if (!newConfig.value.building_block || !newConfig.value.stage || !newConfig.value.code) {
      console.warn('Transport config not created: missing required fields', {
        building_block: newConfig.value.building_block,
        stage: newConfig.value.stage,
        code: newConfig.value.code
      })
      return
    }

    const isAllBuildingBlocks = newConfig.value.building_block === ALL_TRANSPORT_CONFIG_VALUE
    const isAllCodes = newConfig.value.code === ALL_TRANSPORT_CONFIG_VALUE

    const configToCreate: TransportConfig = {
      id: NEW_TRANSPORT_CONFIG_ID,
      calculation: calculationId.value,
      building_block: isAllBuildingBlocks ? null : Number(newConfig.value.building_block),
      stage: String(newConfig.value.stage),
      transport_category: resolveTransportCategory(newConfig.value.transport_category),
      code: isAllCodes ? null : Number(newConfig.value.code),
      distance: Number(newConfig.value.distance) || DEFAULT_TRANSPORT_DISTANCE,
      apply_to_all_building_block: isAllBuildingBlocks,
      apply_to_all_code: isAllCodes
    }

    await transportConfigStore.create(configToCreate)
    newConfig.value = createEmptyTransportConfigModel()
  }

  const debouncedStoreUpdateDynamic = useDebounceFn(async (config: TransportConfig) => {
    await transportConfigStore.update({ ...config })
  }, DEFAULT_DEBOUNCE_DELAY)

  const deleteConfig = async (config: TransportConfig) => {
    const confirmed = await confirmDeleteStore.confirm({
      message: 'Delete this transport configuration?'
    })
    if (!confirmed) return

    await transportConfigStore.delete(config)
  }

  const handleNewBuildingBlockChange = (value: string | number | boolean) => {
    const selectedValue = String(value)
    newConfig.value.building_block = selectedValue
    newConfig.value.apply_to_all_building_block = selectedValue === ALL_TRANSPORT_CONFIG_VALUE
  }

  const handleCodeChange = (value: string | number | boolean) => {
    const selectedValue = String(value)
    newConfig.value.code = selectedValue
    newConfig.value.apply_to_all_code = selectedValue === ALL_TRANSPORT_CONFIG_VALUE
  }

  const handleExistingDistanceChange = async (
    config: TransportConfig,
    value: string | number | null
  ) => {
    const distanceValue =
      typeof value === 'number' ? value : Number(value ?? DEFAULT_TRANSPORT_DISTANCE)
    const updated = {
      ...config,
      distance: isNaN(distanceValue) ? DEFAULT_TRANSPORT_DISTANCE : distanceValue
    }
    await debouncedStoreUpdateDynamic(updated)
  }

  const handleExistingBuildingBlockChange = async (
    config: TransportConfig,
    value: number | string | null
  ) => {
    const updated = {
      ...config,
      building_block: value === ALL_TRANSPORT_CONFIG_VALUE ? null : Number(value),
      apply_to_all_building_block: value === ALL_TRANSPORT_CONFIG_VALUE
    }
    await transportConfigStore.update(updated)
  }

  const handleExistingCodeChange = async (config: TransportConfig, value: string | null) => {
    const updated = {
      ...config,
      code: value === ALL_TRANSPORT_CONFIG_VALUE ? null : Number(value),
      apply_to_all_code: value === ALL_TRANSPORT_CONFIG_VALUE
    }
    await transportConfigStore.update(updated)
  }

  const getBuildingBlockValue = (config: TransportConfig): string | null => {
    if (config.apply_to_all_building_block) return ALL_TRANSPORT_CONFIG_VALUE

    if (config.building_block != null) {
      const value = String(config.building_block)
      if (availableBuildingBlockValueSet.value.has(value)) {
        return value
      }
    }

    return null
  }

  const getCodeValue = (config: TransportConfig): string => {
    return config.apply_to_all_code ? ALL_TRANSPORT_CONFIG_VALUE : String(config.code)
  }

  return {
    isReadOnly,
    transportConfigStore,
    newConfig,
    existingConfigs,
    availableBuildingBlocks,
    availableDikeSections,
    transportNoticeTitle,
    transportMissingMessages,
    handleLoading,
    addTransportConfig,
    debouncedStoreUpdateDynamic,
    deleteConfig,
    handleNewBuildingBlockChange,
    handleCodeChange,
    handleExistingDistanceChange,
    handleExistingBuildingBlockChange,
    handleExistingCodeChange,
    getBuildingBlockValue,
    getCodeValue
  }
}
